import { action, computed, makeObservable, observable } from 'mobx';

import { ILocalStore } from 'stores/interfaces';
import { arrayShiftElementImmutable } from 'utils/arrayShiftElementImmutable';

type PrivateFields = '_order' | '_entities';

/**
 * Тип данных ключа
 */
export type KeyType = string | number;

/**
 * Тип данных коллекция
 */
class Collection<Entity, Raw, Key extends KeyType = string>
  implements ILocalStore
{
  constructor(
    parseItem: (item: Raw) => Entity,
    getEntityKey: (entity: Entity) => Key
  ) {
    this.parseItem = parseItem;

    makeObservable<Collection<Entity, Raw, Key>, PrivateFields>(this, {
      _order: observable.ref,
      _entities: observable.ref,

      order: computed,
      entities: computed,
      isEmpty: computed,

      setOrder: action.bound,
      shiftElement: action.bound,

      setEntities: action.bound,
      parseCollection: action.bound,
      deleteItemFromCollection: action.bound,
      push: action.bound,
      rebuildCollection: action.bound,
      destroy: action.bound,
    });

    this.getEntityKey = getEntityKey;
  }

  /**
   * Порядок следования элементов (массив айдишников)
   */
  private _order: Key[] = [];

  /**
   * объект с сущностями
   */
  private _entities: Record<Key, Entity> = {} as Record<Key, Entity>;

  /**
   * Функция парсинга данных
   */
  readonly parseItem: (item: Raw) => Entity;

  readonly getEntityKey: (entity: Entity) => Key;

  /**
   * Является ли коллекция пустой
   */
  get isEmpty(): boolean {
    return this._order.length === 0;
  }

  get order(): Key[] {
    return this._order;
  }

  get entities(): Record<Key, Entity> {
    return this._entities;
  }

  /**
   * Функция, создающая массив сущностей по порядку (нужно указать свой калбек для обработки элементов)
   * @param callback - колбек для обработки значения элемента (например нормализация каждого элемента)
   */
  linearizeCollection<R>(callback: (item: Entity) => R): R[] {
    return this.order.map((key) => {
      return callback(this.entities[key]);
    });
  }

  /**
   * Функция, которая переставляет элементы между собой
   * */
  shiftElement(initialPosition: number, finalPosition: number): void {
    this.setOrder(
      arrayShiftElementImmutable<Key>(
        this._order,
        initialPosition,
        finalPosition
      )
    );
  }

  setOrder(newOrder: Key[]): void {
    this._order = newOrder;
  }

  setEntities(newEntities: Record<Key, Entity>): void {
    this._entities = newEntities;
  }

  /**
   * Пересобрать коллекцию по массиву элементов коллекций
   * @param arr
   */
  rebuildCollection(arr: Entity[]): void {
    const order: Key[] = [];

    const newEntities = arr.reduce<Record<Key, Entity>>((acc, next) => {
      const id = this.getEntityKey(next);

      order.push(id);

      acc[id] = next;

      return acc;
    }, {} as Record<Key, Entity>);

    this.setEntities(newEntities);
    this.setOrder(order);
  }

  /**
   * Функция парсинга коллекции
   * @param items
   */
  parseCollection(items: Raw[]): void {
    const newOrder: Key[] = [];

    const newEntities = items.reduce<Record<Key, Entity>>((acc, next) => {
      const parsedItem = this.parseItem(next);

      const id = this.getEntityKey(parsedItem);

      acc[id] = parsedItem;
      newOrder.push(id);

      return acc;
    }, {} as Record<Key, Entity>);

    this.setOrder(newOrder);
    this.setEntities(newEntities);
  }

  getItemFromCollection(id: Key): Entity | null {
    return this.entities[id] || null;
  }

  /**
   * Удаление данных из коллекции
   * @param id
   */
  deleteItemFromCollection(id: Key): Entity {
    const newOrder = this.order.filter((key) => key !== id);

    const newItems = { ...this.entities };

    const result = newItems[id];

    delete newItems[id];

    this.setEntities(newItems);
    this.setOrder(newOrder);

    return result;
  }

  map<N>(callback: (element: Entity) => N): N[] {
    return this.order.map((id) => {
      const entity = this.entities[id];

      return callback(entity);
    });
  }

  mapWithFilter<N>({
    transform,
    filter,
  }: {
    transform: (element: Entity) => N;
    filter: (element: N) => boolean;
  }): N[] {
    return this.order.reduce((acc, curr) => {
      const entity = transform(this.entities[curr]);

      if (filter(entity)) {
        return acc.concat([entity]);
      }

      return acc;
    }, [] as N[]);
  }

  /**
   * Добавление данных в коллекцию
   * @param element
   */
  push(element: Entity): void {
    const id = this.getEntityKey(element);

    this.entities[id] = element;
    this.order.push(id);
  }

  destroy() {
    // @ts-ignore
    // Дестроим элементы
    this.linearizeCollection((elem) => elem.destroy?.());
    this.setEntities({} as Record<Key, Entity>);
    this.setOrder([]);
  }
}

export default Collection;
